import { z } from 'zod'

/**
 * batch-58 uitdienst-verlof (FO-424) - de DTO's van de verlof-stap in de
 * uitdienst-flow.
 *
 * De stap is INFORMATIEF (aanname A1): de dialoog toont vooraf per aanvraag wat
 * er gaat gebeuren; doorgaan voert alles automatisch uit. Er is bewust geen
 * per-regel-keuze - de einddatum is leidend, er valt niets te kiezen.
 */

/**
 * Lokale spiegels van `UitdienstVerlofOverlap`/`UitdienstVerlofActie`
 * (`@streep/utils`) - types mag utils niet importeren. De unies zijn identiek;
 * de drift-guard is de compiler: de API kent beide en zou een verschil meteen
 * als type-fout melden.
 */
export type UitdienstVerlofOverlap = 'VOOR' | 'DEELS' | 'NA'
export type UitdienstVerlofActie =
  | 'ONGEMOEID'
  | 'INTREKKEN'
  | 'INKORTEN'
  | 'AFWIJZEN'
  | 'WERKLIJST'

/** Eén verlofaanvraag in de bevestigingsstap / het uitvoeringsresultaat. */
export interface UitdienstVerlofRegelDTO {
  aanvraagId: string
  /** ISO-kalenderdagen (yyyy-MM-dd). */
  startDatum: string
  eindDatum: string
  /** `LeaveStatus` als string (de types-package kent de Prisma-enum niet). */
  status: string
  /** Verloftype (`LeaveType`) als string, voor de weergave. */
  type: string
  overlap: UitdienstVerlofOverlap
  actie: UitdienstVerlofActie
  /** Totaal aantal uren van de aanvraag (Decimal-string, 2 decimalen). */
  urenTotaal: string
  /** Uren die terugkomen op het saldo (Decimal-string). 0 bij afwijzen/blokkade-dagen. */
  urenTerug: string
  /** Bij inkorten: de nieuwe einddatum van de aanvraag (= einddatum dienstverband). */
  nieuweEindDatum: string | null
  /** Uitleg bij een werklijst-regel (verloond of niet eenduidig te splitsen). */
  werklijstReden: string | null
}

/** De volledige verlof-stap: wat er staat en wat er gaat gebeuren. */
export interface UitdienstVerlofPlanDTO {
  /** ISO-kalenderdag (yyyy-MM-dd) van de einddatum waarop dit plan is gebaseerd. */
  einddatum: string
  regels: UitdienstVerlofRegelDTO[]
  totalen: UitdienstVerlofTotalenDTO
}

export interface UitdienstVerlofTotalenDTO {
  intrekken: number
  inkorten: number
  afwijzen: number
  werklijst: number
  /** Som van de terug te boeken uren (Decimal-string). */
  urenTerug: string
}

/** Een aanvraag die door een LATERE einddatum onnodig is ingetrokken/ingekort (A2/T7). */
export interface UitdienstVerlofHerstelRegelDTO {
  aanvraagId: string
  /** De ORIGINELE periode zoals die vóór de automatische correctie was. */
  origineelStart: string
  origineelEind: string
  /** Wat er destijds gebeurde. */
  actie: UitdienstVerlofActie
  /** De einddatum die destijds gold. */
  vorigeEinddatum: string
  /** Uren die destijds zijn teruggeboekt (Decimal-string). */
  urenTerug: string
}

/**
 * De verlof-stap zoals de offboarding-cockpit hem toont (FO-391): afgevinkt met
 * tellingen, plus wat er nog handwerk vraagt.
 */
export interface OffboardingVerlofStapDTO {
  /** true zodra de uitdienst-transactie de verlof-stap heeft uitgevoerd. */
  verwerkt: boolean
  /** ISO-timestamp van de laatste verwerking. */
  verwerktOp: string | null
  ingetrokken: number
  ingekort: number
  afgewezen: number
  /** Totaal teruggeboekte uren (Decimal-string). */
  urenTeruggeboekt: string
  /** Nog open: verlof dat handmatig gecorrigeerd moet worden (T5). */
  werklijst: UitdienstVerlofRegelDTO[]
  /** T7 - door een latere einddatum onnodig geworden correcties (handmatig herstel). */
  onnodigeCorrecties: UitdienstVerlofHerstelRegelDTO[]
  /**
   * batch-58 nazorg - de automatische verwerking is GEFAALD en het verlof na de
   * einddatum staat nog geboekt. De uitdienst-mutatie is dan al gecommit (de
   * oude 409-blokkade is een waarschuwing geworden), dus dit MOET zichtbaar
   * zijn: zonder dit veld rendert de kaart niets en lijkt er niets aan de hand.
   */
  verwerkingGefaald: boolean
}

/** Querystring van de preview-route: de beoogde einddatum (yyyy-MM-dd). */
export const uitdienstVerlofPreviewQuerySchema = z.object({
  einddatum: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Verwacht een datum als jjjj-mm-dd')
    .describe('Beoogde einddatum van het dienstverband'),
})

export type UitdienstVerlofPreviewQuery = z.infer<typeof uitdienstVerlofPreviewQuerySchema>
